import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { PageResp } from "./page";

/**
 * 자유 어휘 사전 조회 경계. `GET /v1/label-terms`·`GET /v1/label-term-keys`.
 *
 * 카탈로그(`/v1/labels`)가 받지 않는 열린 어휘를 조회하는 자원이다. 어휘는 키와 값의 쌍이다.
 * 소비자는 다음과 같다:
 * - 리뷰 화면의 키 자동완성(`searchLabelTermKeys`)
 * - 리뷰 화면의 값 자동완성(`term_key` + `search`)
 * - 큐레이션 큐(`unmapped`)
 *
 * **자동완성이 없으면 사전이 오염된다.** 서버는 키를 소문자·`_` 로 접고 값을 소문자·공백
 * 합치기로 접는다. 그래서 `Gori` 는 합쳐지지만 `obj` / `object` 와 `time out` / `timeout` 은
 * 다른 행이 된다. 한 번 나뉜 사전 행을 합칠 방법이 없다. 입력할 때 이미 있는 키와 값을 보여
 * 주는 것이 유일한 방어다.
 *
 * 계약(backend `service/label/schema.py::LabelTermResp`·`LabelTermKeyResp`)과 1:1 로 맞는다.
 */
export type LabelTerm = {
  // 정규화한 키. 예) `object`
  term_key: string;
  // 대표 원문. 사전에 먼저 들어온 표기다. 나중 표기가 덮지 않는다.
  term_value: string;
  // 값의 사전 정규화형(lowercase + 공백 합치기). 입력이 어떤 값으로 바뀌는지 보여 준다. 그러면
  // 사용자가 새 표기를 덜 만든다.
  normalized: string;
  // 연결된 온톨로지 항. null 이면 매핑되지 않은 것이다. 이 항목들이 큐레이션 큐다(오류가 아니다).
  canonical_key: string | null;
  canonical_value: string | null;
  // 이 어휘가 붙은 에피소드 수. 사용자는 이 값으로 자주 쓰는 표기를 알아본다.
  episode_count: number;
};

export type LabelTermQuery = {
  // 정규화한 키로 좁힌다. `object` 칸의 자동완성이 `reason` 값을 제안하면 안 된다.
  term_key?: string;
  // 원문·정규화형 부분일치. 빈 문자열이면 서버는 필터를 적용하지 않는다.
  search?: string;
  // 온톨로지에 연결되지 않은 어휘만 조회한다. 큐레이션 큐가 쓰는 축이다.
  unmapped?: boolean;
  size?: number;
};

// 사전에 있는 키 한 개다.
export type LabelTermKey = {
  term_key: string;
  // 이 키로 등록된 값의 종류 수(사전 행 수). 에피소드 부여 수가 아니다.
  term_count: number;
};

export type LabelTermKeyQuery = {
  // 키 부분일치. 서버가 키 규칙과 같게 접는다. 빈 문자열이면 필터를 적용하지 않는다.
  search?: string;
  limit?: number;
};

// 자동완성이 한 번에 표시할 후보 수. 목록이 길면 고르는 것이 입력보다 느려진다.
export const LABEL_TERM_SUGGESTION_SIZE = 20;

// 키 자동완성이 한 번에 받는 키 수. 서버 상한이다. 키는 수십 개라 한 번 받고 화면에서 거른다.
export const LABEL_TERM_KEY_SUGGESTION_LIMIT = 200;

/**
 * 사전을 `(term_key, 정규화형)` 사전순으로 조회한다.
 *
 * 정렬이 빈도순이 아닌 이유는 비용이다. 빈도로 정렬하려면 LIMIT 전에 부여 테이블 전체를
 * 집계해야 한다. 그 비용은 입력할 때마다 쓰기에 너무 크다. `episode_count` 는 서버가 돌려준
 * 페이지에 대해서만 센다.
 */
export async function searchLabelTerms(query: LabelTermQuery = {}): Promise<LabelTerm[]> {
  const response = await apiClient.get<PageResp<LabelTerm>>(apiRoutes.review.terms, {
    params: {
      term_key: query.term_key,
      search: query.search === "" ? undefined : query.search,
      unmapped: query.unmapped,
      size: query.size ?? LABEL_TERM_SUGGESTION_SIZE,
    },
  });
  return response.data.items;
}

/**
 * 사전의 키를 값 종류가 많은 순서로 조회한다. 같으면 키 사전순이다.
 *
 * 서버는 사전(`label_terms`)만 묶어 센다. 부여 테이블은 읽지 않는다.
 */
export async function searchLabelTermKeys(query: LabelTermKeyQuery = {}): Promise<LabelTermKey[]> {
  const response = await apiClient.get<LabelTermKey[]>(apiRoutes.review.termKeys, {
    params: {
      search: query.search === "" ? undefined : query.search,
      limit: query.limit,
    },
  });
  return response.data;
}
